/**
 * Centralized Code Validation Service
 * All document codes, bundle codes, and USN codes scanned anywhere in the app
 * must be validated against this service.
 */

import apiClient from './apiClient';

/**
 * Validate a single code
 * @param {string} code - The code to validate
 * @param {string} codeType - Optional: 'document', 'bundle', 'usn', or null for auto-detect
 * @returns {Promise<{valid: boolean, source: string, message: string}>}
 */
export const validateCode = async (code, codeType = null) => {
  try {
    const response = await apiClient.post('/validate/code', {
      code: code.trim(),
      code_type: codeType
    });
    return {
      valid: true,
      source: response.data.source,
      details: response.data.details,
      message: 'Valid code'
    };
  } catch (error) {
    const errorMsg = error.response?.data?.detail || 'Invalid code - not found in system';
    return {
      valid: false,
      source: null,
      details: null,
      message: errorMsg
    };
  }
};

/**
 * Quick check if a code is valid (GET request)
 * @param {string} code - The code to check
 * @param {string} codeType - Optional: 'document', 'bundle', 'usn'
 * @returns {Promise<{valid: boolean, source: string, message: string}>}
 */
export const quickCheckCode = async (code, codeType = null) => {
  try {
    const params = codeType ? { code_type: codeType } : {};
    const response = await apiClient.get(`/validate/check/${encodeURIComponent(code.trim())}`, { params });
    return {
      valid: response.data.valid,
      source: response.data.source,
      message: response.data.message
    };
  } catch (error) {
    return {
      valid: false,
      source: null,
      message: error.response?.data?.detail || 'Invalid code - not found in system'
    };
  }
};

/**
 * Validate multiple codes at once
 * @param {string[]} codes - Array of codes to validate
 * @param {string} codeType - Optional: 'document', 'bundle', 'usn'
 * @returns {Promise<{validCodes: Array, invalidCodes: Array}>}
 */
export const validateBulkCodes = async (codes, codeType = null) => {
  try {
    const response = await apiClient.post('/validate/bulk', {
      codes: codes.map(c => c.trim()),
      code_type: codeType
    });
    return {
      validCodes: response.data.valid_codes,
      invalidCodes: response.data.invalid_codes,
      totalValid: response.data.total_valid,
      totalInvalid: response.data.total_invalid
    };
  } catch (error) {
    return {
      validCodes: [],
      invalidCodes: codes,
      totalValid: 0,
      totalInvalid: codes.length,
      error: error.response?.data?.detail || 'Failed to validate codes'
    };
  }
};

/**
 * Validate document barcode
 * @param {string} code - Document barcode to validate
 * @returns {Promise<{valid: boolean, message: string}>}
 */
export const validateDocumentCode = async (code) => {
  return validateCode(code, 'document');
};

/**
 * Validate bundle barcode
 * @param {string} code - Bundle barcode to validate
 * @returns {Promise<{valid: boolean, message: string}>}
 */
export const validateBundleCode = async (code) => {
  return validateCode(code, 'bundle');
};

/**
 * Validate USN code
 * @param {string} code - USN code to validate
 * @returns {Promise<{valid: boolean, message: string}>}
 */
export const validateUsnCode = async (code) => {
  return validateCode(code, 'usn');
};

/**
 * Validate USN specifically for Greige Inspection
 * Checks that USN is valid AND marked for 'Other Section' (not FOB)
 * @param {string} code - USN code to validate
 * @returns {Promise<{valid: boolean, message: string, section: string}>}
 */
export const validateGreigeUsn = async (code) => {
  try {
    const response = await apiClient.post('/validate/greige-usn', {
      code: code.trim()
    });
    return {
      valid: true,
      source: response.data.source,
      section: response.data.section,
      message: 'Valid USN for Greige/Other Sections'
    };
  } catch (error) {
    const errorMsg = error.response?.data?.detail || 'Invalid USN - not found in system';
    return {
      valid: false,
      source: null,
      section: null,
      message: errorMsg
    };
  }
};

/**
 * Validate USN specifically for FOB Section
 * Checks that USN is valid AND marked for 'FOB'
 * @param {string} code - USN code to validate
 * @returns {Promise<{valid: boolean, message: string, section: string}>}
 */
export const validateFobUsn = async (code) => {
  try {
    const response = await apiClient.post('/validate/fob-usn', {
      code: code.trim()
    });
    return {
      valid: true,
      source: response.data.source,
      section: response.data.section,
      message: 'Valid USN for FOB Section'
    };
  } catch (error) {
    const errorMsg = error.response?.data?.detail || 'Invalid USN - not found in system';
    return {
      valid: false,
      source: null,
      section: null,
      message: errorMsg
    };
  }
};

// =====================================================
// STAFF ID VALIDATION
// =====================================================

/**
 * Validate Staff ID against Staff Details table
 * @param {string} staffId - Staff ID to validate
 * @returns {Promise<{valid: boolean, staffName: string, message: string}>}
 */
export const validateStaffId = async (staffId) => {
  try {
    const response = await apiClient.post('/validate/staff', {
      staff_id: staffId.trim()
    });
    return {
      valid: true,
      staffId: response.data.staff_id,
      staffName: response.data.staff_name,
      designation: response.data.designation,
      section: response.data.section,
      status: response.data.status,
      message: `Valid Staff: ${response.data.staff_name}`
    };
  } catch (error) {
    const errorMsg = error.response?.data?.detail || 'Invalid Staff ID - not found in system';
    return {
      valid: false,
      staffId: staffId,
      staffName: null,
      designation: null,
      section: null,
      status: null,
      message: errorMsg
    };
  }
};

/**
 * Quick check if a Staff ID is valid (GET request)
 * @param {string} staffId - Staff ID to check
 * @returns {Promise<{valid: boolean, staffName: string, message: string}>}
 */
export const quickValidateStaff = async (staffId) => {
  try {
    const response = await apiClient.get(`/validate/staff/${encodeURIComponent(staffId.trim())}`);
    return {
      valid: response.data.valid,
      staffId: response.data.staff_id,
      staffName: response.data.staff_name,
      designation: response.data.designation,
      section: response.data.section,
      status: response.data.status,
      message: response.data.message
    };
  } catch (error) {
    return {
      valid: false,
      staffId: staffId,
      staffName: null,
      message: error.response?.data?.detail || 'Invalid Staff ID'
    };
  }
};

/**
 * Get list of staff for autocomplete/dropdown
 * @param {string} search - Optional search term
 * @param {string} section - Optional section filter
 * @param {number} limit - Max results (default 100)
 * @returns {Promise<{staff: Array, count: number}>}
 */
export const getStaffList = async (search = null, section = null, limit = 100) => {
  try {
    const params = { limit, status: 'active' };
    if (search) params.search = search;
    if (section) params.section = section;
    
    const response = await apiClient.get('/validate/staff-list', { params });
    return {
      staff: response.data.staff || [],
      count: response.data.count || 0
    };
  } catch (error) {
    console.error('Error fetching staff list:', error);
    return {
      staff: [],
      count: 0
    };
  }
};

export default {
  validateCode,
  quickCheckCode,
  validateBulkCodes,
  validateDocumentCode,
  validateBundleCode,
  validateUsnCode,
  validateGreigeUsn,
  validateFobUsn,
  validateStaffId,
  quickValidateStaff,
  getStaffList
};
